import { AlertOctagon, AlertTriangle, TrendingUp } from 'lucide-react';
import { getSection } from '../utils';
import { formatCurrencyFr } from '@/lib/formatters';

const PRIORITIES = [
  {
    key: 'critique',
    label: 'Priorité critique',
    icon: AlertOctagon,
    color: '#8B0028',  // cabinet-bordeaux
  },
  {
    key: 'surveiller',
    label: 'À surveiller',
    icon: AlertTriangle,
    color: '#B5862A',  // cabinet-gold
  },
  {
    key: 'positif',
    label: 'Bonne dynamique',
    icon: TrendingUp,
    color: '#4A6B3A',  // cabinet-forest
  },
];

function AlertCard({ priority, alert }) {
  const Icon = priority.icon;
  return (
    <div
      className="bg-cabinet-paper border-y border-r border-cabinet-border shadow-sm p-6"
      style={{ borderLeft: `3px solid ${priority.color}` }}
      data-testid={`alert-card-${priority.key}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} style={{ color: priority.color }} />
        <span
          className="text-[9px] uppercase tracking-[0.22em] font-medium"
          style={{ color: priority.color }}
        >
          {priority.label}
        </span>
      </div>
      {alert ? (
        <>
          <h4 className="font-playfair text-[15px] text-cabinet-ink leading-snug mb-2">
            {alert.title || alert.label || 'Alerte'}
          </h4>
          {alert.impact_eur != null && (
            <div className="text-[12px] text-cabinet-ink-secondary leading-relaxed">
              Impact estimé · <span className="font-medium text-cabinet-ink">{formatCurrencyFr(Math.abs(alert.impact_eur))} / an</span>
            </div>
          )}
          {alert.summary && (
            <p className="text-[12px] text-cabinet-ink-secondary leading-[1.7] mt-2">{alert.summary}</p>
          )}
        </>
      ) : (
        <p className="font-playfair italic text-[12px] text-cabinet-ink-soft leading-[1.7]">
          Aucune alerte de ce niveau ce mois-ci.
        </p>
      )}
    </div>
  );
}

/**
 * Section III — Trois alertes du mois.
 * Carte par priorité : critique (bordeaux), à surveiller (gold), bonne dynamique (forest).
 *
 * Les alertes proviennent en priorité de `section.content_data.alerts`
 * (structure attendue : [{priority, title, impact_eur, summary}, ...]).
 * Fallback : les 3 premières entrées de `key_figures` mappées par ordre
 * (critique, surveiller, positif).
 */
export default function AlertsRow({ report }) {
  const section = getSection(report, 'alerts');
  const data = section?.content_data || {};

  const rawAlerts = Array.isArray(data.alerts)
    ? data.alerts
    : Array.isArray(data.key_figures)
      ? data.key_figures.slice(0, 3)
      : [];

  const byPriority = {};
  rawAlerts.forEach((alert, idx) => {
    const fallbackKey = idx === 0 ? 'critique' : idx === 1 ? 'surveiller' : 'positif';
    const key = alert.priority || fallbackKey;
    if (!byPriority[key]) byPriority[key] = alert;
  });

  return (
    <section data-testid="alerts-row">
      <div className="text-[10px] text-cabinet-bordeaux uppercase tracking-[0.3em] font-medium mb-2">
        III — Signaux clés
      </div>
      <h2 className="font-playfair text-[22px] text-cabinet-ink mb-3">
        Trois alertes du mois
      </h2>
      <p className="font-playfair italic text-[14px] text-cabinet-ink-secondary mb-8 max-w-2xl">
        Ce que nos analystes ont détecté en priorité dans vos données du mois.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PRIORITIES.map((priority) => (
          <AlertCard key={priority.key} priority={priority} alert={byPriority[priority.key]} />
        ))}
      </div>
    </section>
  );
}
